import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useRouter } from 'next/router';
import { Container } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { OfferingType } from 'typings/markets/api';
import { motion } from 'framer-motion';
import RootLayout from '@/components/layout/RootLayout';
import BreadCrumbs from '@/components/navigation/BreadCrumbs';
import { RootState } from '@/store/rootReducer';
import { AppDispatch } from '@/store/store';
import { getOffering } from '@/features/markets/api/slice';
import DetailView from '@/features/markets/offering-details';
import Seo from '@/components/page-components/seo';
import useAuth from '@/hooks/useAuth';
import SummaryController from '@/features/markets/offering-details/v2/Summary';
import Features from '@/features/markets/offering-details/features';
import { PAGE_TRANSITIONS } from '@/constants';
import global from '../../../.global.json';

/* const useStyles = makeStyles((theme) => ({
  root: {

  },
})); */

interface Props{

}
const defaultProps = {
};

const BREADCRUMB_LINKS = [
  { href: '/markets/offerings', i18nKey: 'Offerings', verbatim: false },
];

const OfferingDetails = () => {
  const { query, isFallback } = useRouter();
  const dispatch = useDispatch() as AppDispatch;
  const {
    offering,
  } = useSelector((state: RootState) => state.markets);

  useEffect(() => {
    dispatch(getOffering({ slug: query.slug as string }));
  }, []);

  if (isFallback) {
    return null;
  }
  return (
    <>
      {offering.data && (
        <DetailView withMarginTop />
      )}
    </>
  );
};

OfferingDetails.defaultProps = defaultProps as unknown as Partial<Props>;

interface WrapperProps {
  offering: OfferingType
}

const OfferingDetailsWrapper = ({ offering }: WrapperProps) => {
  useAuth({ redirect: false });
  const token = useSelector((state: RootState) => state.auth.token);
  const reduxOffering = useSelector((state: RootState) => state.markets.offering.data);

  return (
    <>
      <RootLayout hasFooter>
        {token && (
        <BreadCrumbs
          links={BREADCRUMB_LINKS}
          currentItem={
              { i18nKey: offering.company_name as string, verbatim: true }
            }
        />
        )}
        <motion.div
          variants={PAGE_TRANSITIONS} // Pass the variant object into Framer Motion
          initial="hidden" // Set the initial state to variants.hidden
          animate="enter" // Animated state to variants.enter
          exit="exit" // Exit state (used later) to variants.exit
          transition={{ type: 'linear' }}
        >
          <Container>
            <SummaryController
              withMarginTop
              offering={(reduxOffering && reduxOffering.slug === offering.slug)
                ? reduxOffering : offering}
              showFav={!!reduxOffering}
            />
            {/* sign up CTA goes here */}
            {token ? <OfferingDetails /> : (
              <>
                <Features />
              </>
            )}
          </Container>
        </motion.div>
      </RootLayout>
    </>
  );
};
const OfferingSEOWrapper = ({ offering }: WrapperProps) => (
  <>
    <Seo
      metadata={{
        metaTitle: offering.company_name,
        metaDescription: `${offering.company_name} is crowdfunding on ${offering.platform.name}`,
      }}
    />
    <OfferingDetailsWrapper offering={offering} />
  </>
);

interface Params {
  locale: string
  params: {
    slug: string
  }
}
interface StatisPathsParams {
  locales: string[]
}

// getStaticPaths
// oferta-1 --> compiled
// oferta-2 --> compiled
// resto --> fallback: true
// oferta-3 --> compiled on first request
// ISR --> 3600s

const STATIC_PATH_LIM = 50;

export async function getStaticPaths({ locales }: StatisPathsParams) {
  const {
    data: { hot, new: newItems, closing_soon: closingSoon },
  } = await axios.get(`https://api.wixdom.io/api/markets/offering-slugs/?limit=${STATIC_PATH_LIM}`);
  // } = await axios.get(`https://api.wixdom.io/api/markets/offering-slugs/`);
  // join arrays and filter out unique vals
  const slugs = Array.from(new Set([...hot, ...newItems, ...closingSoon]));
  const paths = locales.reduce(
    (acc: {params: {slug: string}, locale: string}[], next: string) => [
      ...acc,
      ...slugs.map((path) => ({
        params: {
          slug: path,
        },
        locale: next,
      })),
    ],
    [],
  );
  return { paths, fallback: 'blocking' };
}

export async function getStaticProps({ locale, params }: Params) {
  const { data: offering } = await axios.get(`https://api.wixdom.io/api/markets/offering-public/${params.slug}/`);
  // const offering = await res.json();
  return {
    props: {
      global,
      ...await serverSideTranslations(locale, ['common', 'markets', 'components']),
      offering,
    },
    revalidate: 120,
  };
}

export default OfferingSEOWrapper;
